import { useState } from "react";
import { Card, IconButton } from "@mui/material";
import { Edit, Delete as DeleteIcon } from "components/icons";
import { handleAxiosError } from "app/services/app.error.handler";
import { loadClientMappingsPageFromBackend, deleteClientMapping } from "app/services/client.service";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import { clientMappingPageActions } from "store/client-mapping-page-slice";
import { useServerPaginatedGrid } from "app/hooks/useServerPaginatedGrid";
import { PaginatedDataGrid } from "app/components/common/PaginatedDataGrid";
import { ConfirmDialog } from "../ConfirmDialog";
import AddClientMappingDialog from "./AddClientMappingDialog";

const obj = {
  userId: null,
  clientId: null,
};

// Only the mapping's own DB columns are sortable server-side.
const SORT_FIELD_MAP = {
  id: "id",
};

const AllClientMappingsView = () => {
  const [open, setOpen] = useState(false);
  const [editObject, setEditObject] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [deleteObj, setDeleteObj] = useState(obj);

  const {
    rows,
    totalElements,
    page,
    pageSize,
    sortBy,
    sortDirection,
    searchQuery,
    loading,
    fetchPage,
    handlePaginationModelChange,
    handleSortModelChange,
    handleSearchChange,
  } = useServerPaginatedGrid({
    selector: (state) => state.clientMappingPage,
    actions: clientMappingPageActions,
    fetchFn: loadClientMappingsPageFromBackend,
  });

  const editEventHandler = (data) => {
    setEditObject(data);
    setOpen(true);
  };

  const confirmDeleteDialogHandler = (data) => {
    setConfirmationMessage(`Are you sure you want to delete this Client User Mapping?`);
    setDeleteObj({
      userId: parseInt(data.userId),
      clientId: parseInt(data.clientId),
    });
    setConfirmOpen(true);
  };

  const deleteHandler = async () => {
    await deleteClientMapping(deleteObj)
      .then(() => fetchPage())
      .catch((error) => handleAxiosError(error));
  };

  const columns = [
    {
      field: "actions",
      type: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      cellClassName: "actions",
      getActions: (obj) => [
        <IconButton aria-label="Edit" onClick={() => editEventHandler(obj.row)} color="inherit">
          <Edit />
        </IconButton>,
        <IconButton aria-label="Delete" onClick={() => confirmDeleteDialogHandler(obj.row)} color="inherit">
          <DeleteIcon />
        </IconButton>,
      ],
    },
    { field: "id", headerName: "Id", width: 120 },
    { field: "name", headerName: "Name", width: 250, sortable: false, valueGetter: (value, row) => row.user?.fullName },
    { field: "email", headerName: "Email", width: 250, sortable: false, valueGetter: (value, row) => row.user?.email },
    { field: "client", headerName: "Code", width: 150, sortable: false, valueGetter: (value, row) => row.client?.code },
  ];

  return (
    <div style={{ width: "100%" }}>
      <Card>
        <MDBox
          mx={2}
          mt={-3}
          py={1}
          px={2}
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
        >
          <MDTypography variant="h6" color="white">
            Client's Manager Mappings
          </MDTypography>
        </MDBox>
        <MDBox height={500} mb={0} sx={{ overflowX: "auto" }}>
          <PaginatedDataGrid
            rows={rows}
            columns={columns}
            totalElements={totalElements}
            page={page}
            pageSize={pageSize}
            sortBy={sortBy}
            sortDirection={sortDirection}
            searchQuery={searchQuery}
            isLoading={loading}
            sortFieldMap={SORT_FIELD_MAP}
            defaultSortBy="id"
            defaultSortDirection="ASC"
            onPaginationModelChange={handlePaginationModelChange}
            onSortModelChange={handleSortModelChange}
            onSearchChange={handleSearchChange}
            onAdd={() => {
              setEditObject(null);
              setOpen(true);
            }}
            addLabel="Add Client's Manager"
            columnVisibilityModel={{ id: false }}
          />
        </MDBox>
      </Card>
      <AddClientMappingDialog
        open={open}
        setOpen={setOpen}
        refreshActivitiesDataHandler={() => fetchPage()}
        editObject={editObject}
      />
      <ConfirmDialog
        title={"Delete This Client User Mapping?"}
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={deleteHandler}
        yesButtonLabel="Delete"
        noButtonLabel="Cancel"
      >
        {confirmationMessage}
      </ConfirmDialog>
    </div>
  );
};

export default AllClientMappingsView;
